import { createReducer, on } from '@ngrx/store';
import { UserStoriesV2Actions } from '../actions/userstoriesv2.action';

export interface UserStory {
  id: string;
  name: string;
  processId: string;
  type: string;
  description?: string;
  children?: UserStory[];
  [key: string]: any;
}

export interface ProcessNode {
  processId: string;
  name: string;
  children: UserStory[];
  [key: string]: any;
}

export interface UserStoriesV2State {
  processHierarchy: ProcessNode[];
}

export const initialState: UserStoriesV2State = {
  processHierarchy: []
};

export const userStoriesV2Reducer = createReducer<UserStoriesV2State>(
  initialState,
  //set the full tree of processes + stories/epics
  on(UserStoriesV2Actions.setProcessHierarchy, (state, { processHierarchy }) => ({
    ...state,
    processHierarchy
  })),
  on(UserStoriesV2Actions.addProcessToHierarchy, (state, { process }) => {
    const previousNodeSid = process.previousNodeSid;
    const parentNodeIdx = state.processHierarchy.findIndex((p) => p.sid === previousNodeSid);
    const updatedHierarchy = [
      ...state.processHierarchy.slice(0, parentNodeIdx + 1),
      process,
      ...state.processHierarchy.slice(parentNodeIdx + 1)
    ];
    return {
      ...state,
      processHierarchy: [...updatedHierarchy]
    };
  }),
  on(UserStoriesV2Actions.updateProcessToHierarchy, (state, { process }) => {
    const updatedHierarchy = state.processHierarchy.map((p) => {
      if (p.sid === process.sid) {
        return {
          ...p,
          ...process
        };
      }
      return p;
    });
    return {
      ...state,
      processHierarchy: updatedHierarchy
    };
  }),
  on(UserStoriesV2Actions.deleteProcessFromHierarchy, (state, { processId }) => {
    const updatedHierarchy = state.processHierarchy.filter((p) => p.sid !== processId);
    //children of deleted process will be lost, need to handle that in future
    return {
      ...state,
      processHierarchy: updatedHierarchy
    };
  }),
  on(UserStoriesV2Actions.setUserStoriesForProcess, (state, { processId, userStories }) => {
    const updatedHierarchy = state.processHierarchy.map((p) => {
      if (p.sid === processId) {
        return {
          ...p,
          children: userStories
        };
      }
      return p;
    });
    return {
      ...state,
      processHierarchy: updatedHierarchy
    };
  }),
  on(UserStoriesV2Actions.addUserStoryToProcess, (state, { userStory }) => {
    const parentNodeSid = userStory.parentNodeSid;
    const parentNode = state.processHierarchy.filter((p) => p.sid === parentNodeSid)[0];
    const previousNodeSid = userStory.previousNodeSid;
    const childrens = parentNode.children ?? [];
    const previousNodeIdx = childrens.findIndex((c) => c.sid === previousNodeSid);
    const updatedChildrenHierarchy = [
      ...childrens.slice(0, previousNodeIdx + 1),
      userStory,
      ...childrens.slice(previousNodeIdx + 1)
    ];

    return {
      ...state,
      processHierarchy: state.processHierarchy.map((p) => {
        if (p.sid === parentNodeSid) {
          return {
            ...p,
            children: updatedChildrenHierarchy
          };
        }
        return p;
      })
    };
  }),
  on(UserStoriesV2Actions.updateUserStoryInProcess, (state, { userStory }) => {
    const parentNodeSid = userStory.parentNodeSid;
    const parentNode = state.processHierarchy.filter((p) => p.sid === parentNodeSid)[0];
    const updatedChildrenHierarchy = (parentNode.children ?? []).map((c) => {
      if (c.sid === userStory.sid) {
        return {
          ...c,
          ...userStory
        };
      }
      return c;
    });
    return {
      ...state,
      processHierarchy: state.processHierarchy.map((p) => {
        if (p.sid === parentNodeSid) {
          return {
            ...p,
            children: updatedChildrenHierarchy
          };
        }
        return p;
      })
    };
  }),
  on(UserStoriesV2Actions.deleteUserStoryFromProcess, (state, { userStory }) => {
    const parentNode = state.processHierarchy.filter((p) => p.sid === userStory.parentNodeSid)[0];
    const updatedChildrenHierarchy = (parentNode.children ?? []).filter((c) => c.sid !== userStory.sid);
    return {
      ...state,
      processHierarchy: state.processHierarchy.map((p) => {
        if (p.sid === parentNode.sid) {
          return {
            ...p,
            children: updatedChildrenHierarchy
          };
        }
        return p;
      })
    };
  })
);
